import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import styles from './Callout.module.css';

type CalloutVariant = 'info' | 'warning' | 'error' | 'success';

// `title` is the bold first line, not the HTML tooltip attribute.
export interface CalloutProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CalloutVariant;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}

const InfoIcon = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 7.4V11.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.9" fill="currentColor" />
  </svg>
);

const WarningIcon = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8 2.1 14.7 13.4a.6.6 0 0 1-.52.9H1.82a.6.6 0 0 1-.52-.9L8 2.1Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M8 6.4v3.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="12" r="0.9" fill="currentColor" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.9 5.9 10.1 10.1M10.1 5.9 5.9 10.1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const SuccessIcon = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.3 8.2 7.2 10.1 10.7 6.1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VARIANT_ICONS: Record<CalloutVariant, () => ReactNode> = {
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  success: SuccessIcon,
};

/**
 * Inline notice box (v1.1): a token-driven surface tint + hairline border with
 * a leading status icon. `info` is the neutral ink treatment; `warning`,
 * `error` and `success` tint with the amber, red and green ramps. `warning`
 * and `error` are announced as alerts, the rest as status. `title` and
 * `description` stack a bold line over the text at the box's own size; other
 * content is free-form — compose links/actions as children.
 */
export const Callout = ({
  variant = 'info',
  title,
  description,
  children,
  className,
  ...divProps
}: CalloutProps) => {
  const Icon = VARIANT_ICONS[variant];
  return (
    <div
      role={variant === 'warning' || variant === 'error' ? 'alert' : 'status'}
      className={classNames(styles.callout, styles[variant], className)}
      {...divProps}
    >
      <Icon />
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {description && <div>{description}</div>}
        {children}
      </div>
    </div>
  );
};
